import React, {useState} from 'react'
import {motion} from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import Card from '../ui/Card'

const {FiGift, FiStar, FiEdit3, FiTrash2, FiMoreVertical} = FiIcons

const RewardCard = ({reward, childPoints = 0, onRedeem, canRedeem = false, onEdit, onDelete, showActions = false}) => {
  const [showActionsMenu, setShowActionsMenu] = useState(false)

  const pointsNeeded = Math.max(0, reward.points_required - childPoints)
  const canAfford = childPoints >= reward.points_required

  return (
    <Card className="p-4 hover:shadow-xl transition-all duration-300 relative">
      {/* Actions Menu - Parent View Only */}
      {showActions && (
        <div className="absolute top-4 right-4">
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowActionsMenu(!showActionsMenu)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <SafeIcon icon={FiMoreVertical} className="text-gray-500 dark:text-gray-400" />
            </Button>

            {showActionsMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10 min-w-[120px]"
              >
                <button
                  onClick={() => {
                    onEdit?.(reward)
                    setShowActionsMenu(false)
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <SafeIcon icon={FiEdit3} className="text-xs text-blue-500 dark:text-blue-400" />
                  Edit Reward
                </button>
                <button
                  onClick={() => {
                    onDelete?.(reward)
                    setShowActionsMenu(false)
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                >
                  <SafeIcon icon={FiTrash2} className="text-xs text-red-500 dark:text-red-400" />
                  Delete Reward
                </button>
              </motion.div>
            )}
          </div>
        </div>
      )}

      <div className="text-center">
        <div className="text-4xl mb-3">
          {reward.emoji || '🎁'}
        </div>

        <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg mb-2">
          {reward.title}
        </h3>

        {reward.description && (
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
            {reward.description}
          </p>
        )}

        <div className="flex items-center justify-center gap-2 mb-4">
          <Badge 
            variant={canAfford ? "success" : "gray"} 
            className="flex items-center gap-1"
          >
            <SafeIcon icon={FiStar} className="text-sm" />
            <span>{reward.points_required} points</span>
          </Badge>
        </div>

        {!canAfford && pointsNeeded > 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            {pointsNeeded} more points needed
          </p>
        )}

        {canRedeem && (
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              variant={canAfford ? "success" : "outline"}
              size="sm"
              onClick={() => onRedeem(reward)}
              disabled={!canAfford}
              className="w-full"
            >
              <SafeIcon icon={FiGift} className="text-sm" />
              {canAfford ? 'Redeem Now!' : 'Not Enough Points'}
            </Button>
          </motion.div>
        )}
      </div>
    </Card>
  )
}

export default RewardCard